import React, { useState, useEffect, useCallback } from "react";
import { BarChart3, Users, Send, Clock, ArrowUpRight, ChevronDown, Download, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadsAnalyticsSection = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRecent, setShowRecent] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/stats`);
      setStats(res.data);
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads_export.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { /* */ }
  };

  const deleteLead = async (phone) => {
    if (!window.confirm(`Supprimer le lead ${phone} ?`)) return;
    try {
      await axios.delete(`${API}/admin/leads/${encodeURIComponent(phone)}`);
      loadStats();
    } catch { /* */ }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border p-6">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" /> Analytics Leads
        </h2>
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const cards = [
    { label: "Total leads", value: stats.total, icon: Users, color: "text-primary" },
    { label: "Envoyés CRM", value: stats.sent, icon: Send, color: "text-green-600" },
    { label: "En attente", value: stats.pending, icon: Clock, color: "text-orange-500" },
    { label: "Complétés (étape 2)", value: stats.complete, icon: ArrowUpRight, color: "text-accent" },
  ];

  return (
    <div className="bg-card rounded-lg border p-6" data-testid="leads-analytics">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" /> Analytics Leads
        </h2>
        <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-leads">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="bg-muted/50 rounded-lg p-4 text-center" data-testid={`stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
              <Icon className={`w-5 h-5 mx-auto mb-2 ${card.color}`} />
              <p className="text-2xl font-black">{card.value}</p>
              <p className="text-xs text-muted-foreground">{card.label}</p>
            </div>
          );
        })}
      </div>

      {/* Funnel */}
      <div className="bg-muted/30 rounded-lg p-4 mb-6">
        <p className="text-sm font-semibold mb-3">Funnel de conversion</p>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <div className="bg-primary/20 rounded h-6 relative overflow-hidden">
              <div
                className="bg-primary h-6 rounded"
                style={{ width: stats.total > 0 ? "100%" : "0%" }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Étape 1 : {stats.step1_partial + stats.complete}</p>
          </div>
          <span className="text-muted-foreground text-xs">→</span>
          <div className="flex-1">
            <div className="bg-accent/20 rounded h-6 relative overflow-hidden">
              <div
                className="bg-accent h-6 rounded"
                style={{ width: stats.total > 0 ? `${Math.round((stats.complete / stats.total) * 100)}%` : "0%" }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Complétés : {stats.complete} ({stats.total > 0 ? Math.round((stats.complete / stats.total) * 100) : 0}%)</p>
          </div>
        </div>
      </div>

      {/* By Source */}
      {stats.by_source.length > 0 && (
        <div className="mb-6">
          <p className="text-sm font-semibold mb-3">Par source</p>
          <div className="space-y-2">
            {stats.by_source.map((s) => (
              <div key={s.source} className="flex items-center justify-between text-sm bg-muted/30 rounded px-3 py-2">
                <span className="text-muted-foreground truncate mr-2">{s.source}</span>
                <span className="font-bold whitespace-nowrap">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Leads */}
      {stats.recent_leads.length > 0 && (
        <div>
          <button
            onClick={() => setShowRecent(!showRecent)}
            className="flex items-center gap-2 text-sm font-semibold text-primary hover:underline mb-3"
            data-testid="toggle-recent-leads"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${showRecent ? "rotate-180" : ""}`} />
            Derniers leads ({stats.recent_leads.length})
          </button>
          {showRecent && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-3">Nom</th>
                    <th className="pb-2 pr-3">Téléphone</th>
                    <th className="pb-2 pr-3">Société</th>
                    <th className="pb-2 pr-3">Source</th>
                    <th className="pb-2 pr-3">Statut</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_leads.map((lead, i) => (
                    <tr key={i} className="border-b border-muted/50">
                      <td className="py-2 pr-3 font-medium">{lead.first_name} {lead.last_name}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{lead.phone}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{lead.company || "—"}</td>
                      <td className="py-2 pr-3 text-muted-foreground truncate max-w-[120px]">{lead.source || "—"}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          lead.status === "sent" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
                        }`}>
                          {lead.status === "sent" ? "Envoyé" : "En attente"}
                        </span>
                      </td>
                      <td className="py-2">
                        <button onClick={() => deleteLead(lead.phone)} className="text-red-400 hover:text-red-600 transition-colors" title="Supprimer">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
